import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Home as HomeIcon, ShoppingBag, TreePine, UtensilsCrossed, Menu, X, Mail, Phone, MapPin, Facebook, Instagram, ChevronRight, Star, Truck, Shield, Clock, Settings } from "lucide-react";
import AdminPanel from "./AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Auto seed database
axios.post(`${API}/seed`).catch(() => {});

// Images
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_quick-shop-138/artifacts/qw8qef40_41DF8A24-33B7-4E78-BF53-232D6BBE23F7.png";
const CUTTING_BOARD_1 = "https://customer-assets.emergentagent.com/job_quick-shop-138/artifacts/dyqlhqd3_CB419C67-B22B-4625-8CF6-8D68F52FEDD0.png";
const CUTTING_BOARD_2 = "https://customer-assets.emergentagent.com/job_quick-shop-138/artifacts/mw1eucf1_2FBE923A-4BBE-4048-90B7-6EE4768FB31D.png";

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#1e5631] rounded-full flex items-center justify-center">
              <TreePine className="w-7 h-7 text-white" />
            </div>
            <span className={`text-2xl font-bold font-serif ${isScrolled ? 'text-[#1e5631]' : 'text-white'}`}>
              TNY Store
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              data-testid="nav-home"
              className={`font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#1e5631]' : 'text-white/90 hover:text-white'}`}
            >
              Начало
            </Link>
            <Link 
              to="/category/pesachnici" 
              data-testid="nav-sandboxes"
              className={`font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#1e5631]' : 'text-white/90 hover:text-white'}`}
            >
              Пясъчници
            </Link>
            <Link 
              to="/category/detski-saorazhenia" 
              data-testid="nav-playground"
              className={`font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#1e5631]' : 'text-white/90 hover:text-white'}`}
            >
              Детски съоръжения
            </Link>
            <Link 
              to="/category/daski-za-ryazane" 
              data-testid="nav-cutting-boards"
              className={`font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#1e5631]' : 'text-white/90 hover:text-white'}`}
            >
              Дъски за рязане
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            data-testid="mobile-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-2 rounded-lg ${isScrolled ? 'text-gray-700' : 'text-white'}`}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t shadow-lg" data-testid="mobile-menu">
          <nav className="flex flex-col p-4 gap-4">
            <Link to="/" onClick={() => setIsMenuOpen(false)} className="text-gray-700 font-medium py-2 hover:text-[#1e5631]">
              Начало
            </Link>
            <Link to="/category/pesachnici" onClick={() => setIsMenuOpen(false)} className="text-gray-700 font-medium py-2 hover:text-[#1e5631]">
              Пясъчници
            </Link>
            <Link to="/category/detski-saorazhenia" onClick={() => setIsMenuOpen(false)} className="text-gray-700 font-medium py-2 hover:text-[#1e5631]">
              Детски съоръжения
            </Link>
            <Link to="/category/daski-za-ryazane" onClick={() => setIsMenuOpen(false)} className="text-gray-700 font-medium py-2 hover:text-[#1e5631]">
              Дъски за рязане
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
        <h1 
          data-testid="hero-title"
          className="text-4xl md:text-6xl lg:text-7xl font-serif text-white mb-6 animate-fadeInUp"
        >
          Всичко се изработва ръчно от естествено дърво
        </h1>
        <p 
          data-testid="hero-subtitle"
          className="text-xl md:text-2xl text-white/90 mb-10 animate-fadeInUp animation-delay-200"
        >
          Качество. Здравина. Безопасност за вашите деца.
        </p>
        <Link 
          to="/category/pesachnici"
          data-testid="hero-cta-btn"
          className="inline-flex items-center gap-2 bg-[#1e5631] text-white px-10 py-5 rounded-full text-lg font-semibold transition-all duration-300 hover:bg-[#2d7a47] hover:shadow-2xl hover:shadow-green-900/30 animate-fadeInUp animation-delay-300"
        >
          Разгледай продуктите
          <ChevronRight className="w-5 h-5" />
        </Link>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/70 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

// Categories Section
const CategoriesSection = () => {
  const categories = [
    {
      slug: "pesachnici",
      title: "Пясъчници",
      description: "Дървени пясъчници с капак и пейки. Безопасни, стабилни и устойчиви на външни условия.",
      image: "https://images.unsplash.com/photo-1566140967404-b8b3932483f5?w=800",
      icon: "🏖",
      buttonText: "Виж пясъчници"
    },
    {
      slug: "detski-saorazhenia",
      title: "Детски съоръжения",
      description: "Детски къщички, люлки и съоръжения за двор. Създадени за усмивки и игри на открито.",
      image: HERO_IMAGE,
      icon: "🛝",
      buttonText: "Разгледай съоръжения"
    },
    {
      slug: "daski-za-ryazane",
      title: "Дъски за рязане",
      description: "Масивни дървени дъски за кухнята. Издръжливи, красиви и практични.",
      image: CUTTING_BOARD_1,
      icon: "🔪",
      buttonText: "Виж дъски"
    }
  ];

  return (
    <section data-testid="categories-section" className="py-24 bg-[#f8f5f0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 data-testid="categories-title" className="text-4xl md:text-5xl font-serif text-[#2c2c2c] mb-4">
            Нашите продукти
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Всеки продукт е изработен с внимание към детайла от майстори с дългогодишен опит
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {categories.map((category, index) => (
            <div
              key={category.slug}
              data-testid={`category-card-${category.slug}`}
              className="bg-white rounded-3xl overflow-hidden shadow-lg hover-lift group"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="relative h-72 overflow-hidden">
                <img
                  src={category.image}
                  alt={category.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <span className="absolute top-4 left-4 text-4xl">{category.icon}</span>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-serif text-[#2c2c2c] mb-3">{category.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{category.description}</p>
                <Link
                  to={`/category/${category.slug}`}
                  data-testid={`category-btn-${category.slug}`}
                  className="inline-flex items-center gap-2 bg-[#1e5631] text-white px-6 py-3 rounded-full font-medium transition-all duration-300 hover:bg-[#2d7a47] hover:gap-3"
                >
                  {category.buttonText}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const features = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "100% безопасно",
      description: "Използваме само сертифицирани материали, безопасни за деца"
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Безплатна доставка",
      description: "Доставяме безплатно в цялата страна за поръчки над 200 лв"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Бърза изработка",
      description: "Изработваме и доставяме в срок от 7 до 21 работни дни"
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "Гаранция за качество",
      description: "Предлагаме 2 години гаранция на всички наши продукти"
    }
  ];

  return (
    <section data-testid="features-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              data-testid={`feature-${index}`}
              className="text-center p-6"
            >
              <div className="w-16 h-16 bg-[#1e5631]/10 rounded-2xl flex items-center justify-center mx-auto mb-5 text-[#1e5631]">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-[#2c2c2c] mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      setStatus({ type: 'success', message: 'Успешно се абонирахте! Очаквайте нашите оферти.' });
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        setStatus({ type: 'error', message: 'Този имейл вече е абониран.' });
      } else {
        setStatus({ type: 'error', message: 'Възникна грешка. Моля, опитайте отново.' });
      }
    }
    setIsLoading(false);
  };

  return (
    <section data-testid="newsletter-section" className="py-24 bg-[#1e5631]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Mail className="w-16 h-16 text-white/80 mx-auto mb-6" />
        <h2 data-testid="newsletter-title" className="text-4xl font-serif text-white mb-4">
          Абонирай се и получи отстъпка
        </h2>
        <p className="text-white/80 text-lg mb-10">
          Получавай нови продукти и специални оферти първи.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
          <input
            type="email"
            data-testid="newsletter-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Въведи имейл"
            className="flex-1 px-6 py-4 rounded-full text-gray-800 text-lg focus:outline-none focus:ring-4 focus:ring-white/30"
            required
          />
          <button
            type="submit"
            data-testid="newsletter-submit-btn"
            disabled={isLoading}
            className="bg-[#d4a574] text-[#2c2c2c] px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:bg-[#e5b685] disabled:opacity-70 whitespace-nowrap"
          >
            {isLoading ? 'Изпращане...' : 'Абонирай се'}
          </button>
        </form>

        {status.message && (
          <p 
            data-testid="newsletter-status"
            className={`mt-6 text-lg ${status.type === 'success' ? 'text-green-300' : 'text-red-300'}`}
          >
            {status.message}
          </p>
        )}
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#2c2c2c] text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 bg-[#1e5631] rounded-full flex items-center justify-center">
                <TreePine className="w-7 h-7 text-white" />
              </div>
              <span className="text-2xl font-bold font-serif">TNY Store</span>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Производство на дървени пясъчници, детски къщички и дъски за рязане. 
              Ръчна изработка от естествено дърво. Поръчай директно от производителя.
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#1e5631] transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#1e5631] transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold text-lg mb-5">Продукти</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/category/pesachnici" className="text-gray-400 hover:text-white transition-colors">
                  Пясъчници
                </Link>
              </li>
              <li>
                <Link to="/category/detski-saorazhenia" className="text-gray-400 hover:text-white transition-colors">
                  Детски съоръжения
                </Link>
              </li>
              <li>
                <Link to="/category/daski-za-ryazane" className="text-gray-400 hover:text-white transition-colors">
                  Дъски за рязане
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-5">Контакти</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-gray-400">
                <Phone className="w-5 h-5 text-[#1e5631]" />
                <span>+359 888 123 456</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400">
                <Mail className="w-5 h-5 text-[#1e5631]" />
                <span>info@tny-store.com</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 text-[#1e5631] mt-1" />
                <span>гр. София, България</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 text-center text-gray-500">
          <p>&copy; 2024 TNY Store. Всички права запазени.</p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  useEffect(() => {
    // Seed data on first load
    const seedData = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        if (response.data.length === 0) {
          await axios.post(`${API}/seed`);
        }
      } catch (e) {
        console.error('Error seeding data:', e);
      }
    };
    seedData();
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection />
      <CategoriesSection />
      <FeaturesSection />
      <NewsletterSection />
    </div>
  );
};

// Category Page
const CategoryPage = () => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const categoryNames = {
    'pesachnici': 'Пясъчници',
    'detski-saorazhenia': 'Детски съоръжения',
    'daski-za-ryazane': 'Дъски за рязане'
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // First try to seed if no products
        const allProducts = await axios.get(`${API}/products`);
        if (allProducts.data.length === 0) {
          await axios.post(`${API}/seed`);
        }
        
        const [productsRes, categoryRes] = await Promise.all([
          axios.get(`${API}/products?category=${slug}`),
          axios.get(`${API}/categories/${slug}`)
        ]);
        setProducts(productsRes.data);
        setCategory(categoryRes.data);
      } catch (e) {
        console.error('Error fetching data:', e);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#1e5631] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="category-page" className="min-h-screen pt-24 bg-[#f8f5f0]">
      {/* Header */}
      <div className="bg-[#1e5631] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 data-testid="category-page-title" className="text-4xl md:text-5xl font-serif text-white mb-4">
            {categoryNames[slug] || category?.name}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {category?.description}
          </p>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-xl">Няма налични продукти в тази категория.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                data-testid={`product-card-${product.id}`}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover-lift group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {product.is_featured && (
                    <span className="absolute top-4 right-4 bg-[#d4a574] text-white text-sm px-3 py-1 rounded-full">
                      Популярен
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-serif text-[#2c2c2c] mb-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                  
                  {product.dimensions && (
                    <p className="text-sm text-gray-500 mb-2">
                      <span className="font-medium">Размери:</span> {product.dimensions}
                    </p>
                  )}
                  {product.production_time && (
                    <p className="text-sm text-gray-500 mb-2">
                      <span className="font-medium">Изработка:</span> {product.production_time}
                    </p>
                  )}
                  {product.delivery_info && (
                    <p className="text-sm text-gray-500 mb-4">
                      <span className="font-medium">Доставка:</span> {product.delivery_info}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                    <span className="text-2xl font-bold text-[#1e5631]">{product.price.toFixed(2)} лв</span>
                    <button
                      data-testid={`product-btn-${product.id}`}
                      className="bg-[#1e5631] text-white px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 hover:bg-[#2d7a47]"
                    >
                      Поръчай
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
 function App() {
  return (
    <BrowserRouter>
      <Header />

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/admin" element={<AdminPanel />} />
      </Routes>

      <Footer />
    </BrowserRouter>
  );
}

export default App;